import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { MapPin, Network, Plus } from 'lucide-react';
import { apiPost } from '../api/client.js';
import { useStores, useCreateStore } from '../hooks/useStores.js';
import { useClientStore } from '../store/client.store.js';
import { useAuthStore } from '../store/auth.store.js';
import Table from '../components/Table.jsx';
import Modal from '../components/Modal.jsx';
import Button from '../components/Button.jsx';
import Input from '../components/Input.jsx';
import Badge from '../components/Badge.jsx';
import PageHeader from '../components/PageHeader.jsx';
import NoClientSelected from '../components/NoClientSelected.jsx';
import AddressSearch from '../components/AddressSearch.jsx';
import LocationFields from '../components/LocationFields.jsx';

const syncBadge = (store) => store.mp_store_id
  ? <Badge variant="success">Sync ✓</Badge>
  : <Badge variant="warning">Pendiente</Badge>;

export default function Stores() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const { selectedClientId } = useClientStore();
  const { data, isLoading } = useStores();
  const createMutation = useCreateStore();
  const [open, setOpen] = useState(!!location.state?.autoOpen);
  const [form, setForm] = useState({
    externalId: '', name: '', description: '',
    streetName: '', streetNumber: '', zipCode: '',
    cityName: '', stateName: '', countryId: 'AR',
    latitude: '', longitude: '',
    ...(location.state?.prefill || {}),
  });
  const [err, setErr] = useState(null);

  // Reaccionar a navegaciones desde el mapa (location.key cambia en cada navigate())
  // Necesario porque useState solo lee location.state en el primer mount
  useEffect(() => {
    if (!location.state?.prefill) return;
    setForm(f => ({ ...f, ...location.state.prefill }));
    if (location.state.autoOpen) setOpen(true);
  }, [location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  if (user?.role === 'superadmin' && !selectedClientId) {
    return <NoClientSelected entity="stores y sucursales" />;
  }

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const handleLocationChange = (field, value) => setForm(p => ({ ...p, [field]: value }));

  const handleCreate = () => {
    setErr(null);

    // Validación client-side
    if (!form.externalId.trim()) return setErr('El ID Externo es obligatorio (ej: SUC001, CENTRAL)');
    if (!form.name.trim()) return setErr('El nombre es obligatorio');
    if (!form.streetName.trim() || !form.cityName.trim()) return setErr('La dirección (calle y ciudad) es obligatoria para sincronizar con MercadoPago');
    if (!form.latitude || !form.longitude) return setErr('Las coordenadas son obligatorias. Usá el buscador de dirección para obtenerlas automáticamente');

    const payload = {
      ...form,
      externalId: form.externalId.trim().toUpperCase(),
      latitude: parseFloat(form.latitude),
      longitude: parseFloat(form.longitude),
    };

    createMutation.mutate(payload, {
      onSuccess: (created) => {
        // mpStoreId ya fue procesado por el backend (vincula sin crear nueva en MP)
        const wasImport = !!form.mpStoreId;
        setOpen(false);
        setForm({ externalId: '', name: '', description: '', streetName: '', streetNumber: '', zipCode: '', cityName: '', stateName: '', countryId: 'AR', latitude: '', longitude: '', mpStoreId: '' });
        if (wasImport && created?.id) navigate(`/stores/${created.id}`);
      },
      onError: (e) => {
        const data = e.response?.data;
        if (Array.isArray(data?.error)) {
          // Zod validation errors: mostrar el primer mensaje legible
          const msg = data.error.map(issue => `${issue.path?.join('.') || 'campo'}: ${issue.message}`).join(' · ');
          setErr(msg);
        } else {
          setErr(data?.message || data?.error || 'Error al crear la store');
        }
      },
    });
  };

  const columns = [
    { key: 'external_id', header: 'ID Externo' },
    { key: 'name', header: 'Nombre' },
    { key: 'city_name', header: 'Ciudad' },
    { key: 'status', header: 'Estado', render: (r) => <Badge variant={r.status === 'active' ? 'success' : 'danger'}>{r.status}</Badge> },
    { key: 'mp_sync', header: 'MP Sync', render: syncBadge },
    {
      key: 'actions', header: '',
      render: (r) => (
        <button
          onClick={(e) => { e.stopPropagation(); navigate(`/stores/${r.id}`); }}
          className="text-xs text-accent hover:text-accent-hover whitespace-nowrap flex items-center gap-1"
        >
          Ver / POS →
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <PageHeader icon={MapPin} title="Sucursales" subtitle={data ? `${data.length} registradas` : undefined} color="emerald">
        {user?.role === 'superadmin' && selectedClientId && (
          <Link to={`/clients/${selectedClientId}/mp-map`}>
            <Button variant="secondary"><Network size={14} className="mr-1.5 shrink-0" /> Mapa MP</Button>
          </Link>
        )}
        <Button onClick={() => setOpen(true)}><Plus size={14} className="mr-1.5 shrink-0" /> Nueva sucursal</Button>
      </PageHeader>
      <Table columns={columns} data={data} loading={isLoading} empty="No hay stores registradas" onRowClick={(r) => navigate(`/stores/${r.id}`)} />
      <Modal open={open} onClose={() => setOpen(false)} title="Nueva store" maxWidth="max-w-2xl"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button loading={createMutation.isPending} onClick={handleCreate}>Crear</Button></>}
      >
        {form.mpStoreId && (
          <div className="p-3 rounded-lg border border-emerald-700/40 bg-emerald-900/10 text-xs text-emerald-300 flex items-start gap-2">
            <span className="shrink-0 mt-0.5">🔗</span>
            <span>Importada del mapa MP — se vinculará automáticamente al ID <span className="font-mono">{form.mpStoreId}</span> al guardar.</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Input label="ID Externo *" value={form.externalId} onChange={f('externalId')} placeholder="SUC001" />
            <p className="text-xs text-slate-500 mt-1">Código único de la sucursal (ej: CENTRAL, SUC001)</p>
          </div>
          <Input label="Nombre *" value={form.name} onChange={f('name')} />
        </div>
        <AddressSearch
          lat={form.latitude}
          lon={form.longitude}
          onSelect={(fields) => setForm(f => ({ ...f, ...fields }))}
        />
        <hr className="border-border my-1" />
        <LocationFields form={form} onChange={handleLocationChange} />
        <Input label="Descripción" value={form.description} onChange={f('description')} />
        {err && <p className="text-sm text-red-400">{err}</p>}
      </Modal>
    </div>
  );
}
